import React, { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { graphql } from '@apollo/client/react/hoc'
import { Container, Row, Col } from 'reactstrap'
import './TaskManagementContainer.css'
import { AllBoardsQuery } from './Queries'
import withApolloClient from '../withApolloClient'
import BoardActionsMenu from './TaskComponents/BoardActionsMenu'
import TaskManagementViewContainer from './TaskManagementViewContainer'
import { getDefaultTaskColumnConfig } from './taskTableConfig'

const getTaskBoardColumnConfig = (boards = [], boardId) => {
    const activeBoard = boardId && boardId !== "tasks" ? boards.find(({ node }) => node.id === boardId)?.node || null : null
    const activeBoardColumns = activeBoard?.columnSet?.edges || []
    const customColumns = activeBoardColumns.filter((column) => !column.node.isArchived).map((column) => ({
        id: `custom_column_${column.node.pk}`,
        name: column.node.title,
        sortable: false,
        columnNode: column.node,
        isCustom: true,
        preferenceSettings: {
            isVisible: true,
            canToggleVisibility: true,
            canReorder: true,
        },
    }))

    return {
        activeBoard,
        defaultColumnConfig: [...getDefaultTaskColumnConfig(), ...customColumns],
        tablePreferenceId: activeBoard ? `task_board_${activeBoard.id}_preferences` : "my_tasks_board_preferences",
    }
}


function TaskManagementContainer({ allBoards, loadMoreEntries, currentUser }) {
    const { boardId } = useParams()
    const boards = allBoards?.edges || []
    const loadedBoardCount = boards.length + 1
    const totalBoardCount = (allBoards?.pageInfo?.length || boards.length) + 1
    const { activeBoard, defaultColumnConfig, tablePreferenceId } = useMemo(
        () => getTaskBoardColumnConfig(boards, boardId),
        [boards, boardId])
    const activeBoardTitle = activeBoard?.title || "My Tasks"

    return (
        <div className="TaskManagementContainer">
            <Container fluid>
                <Row>
                    <Col xs="2" className="TaskBoardColumn">
                        <aside className="task-board-sidebar">
                            <div className="task-board-sidebar-header">
                                <div>
                                    <div className="task-board-sidebar-eyebrow">Workspace</div>
                                    <h4 className="task-board-sidebar-title">Boards</h4>
                                </div>
                                <span className="task-board-sidebar-count">{loadedBoardCount}</span>
                            </div>
                            <div className="task-board-sidebar-meta">
                                Showing {loadedBoardCount} of {totalBoardCount} boards
                            </div>
                            <div className="task-board-sidebar-current">
                                <span className="task-board-sidebar-current-label">Viewing</span>
                                <span className="task-board-sidebar-current-value">{activeBoardTitle}</span>
                            </div>
                            <ul className="TaskBoardList">
                                <li className={!boardId || boardId === 'tasks' ? 'active' : ''}>
                                    <div className="board-sidebar-item">
                                        <Link className="board-sidebar-link" to="/boards/tasks/">
                                            <span className="board-sidebar-icon"><i className="fa fa-inbox" aria-hidden="true" /></span>
                                            <span className="board-sidebar-content">
                                                <span className="board-sidebar-title-text">My Tasks</span>
                                                <span className="board-sidebar-subtitle">Personal task queue</span>
                                            </span>
                                        </Link>
                                    </div>
                                </li>
                                {boards.map((boardEdge) => (
                                    <li className={boardId === boardEdge.node.id ? 'active' : ''} key={boardEdge.node.id}>
                                        <div className="board-sidebar-item">
                                            <Link className="board-sidebar-link" to={`/boards/${boardEdge.node.id}/tasks/`}>
                                                <span className="board-sidebar-icon"><i className="fa fa-columns" aria-hidden="true" /></span>
                                                <span className="board-sidebar-content">
                                                    <span className="board-sidebar-title-text">{boardEdge.node.title}</span>
                                                    <span className="board-sidebar-subtitle">
                                                        {boardEdge.node.description || `Board #${boardEdge.node.pk}`}
                                                    </span>
                                                </span>
                                            </Link>
                                            <span className="board-edit-icon board-actions-wrapper" onClick={(e) => e.stopPropagation()}>
                                                <BoardActionsMenu boardId={boardEdge.node.id} />
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {allBoards?.pageInfo?.hasNextPage && (
                                <button type="button" className="task-board-load-more" onClick={loadMoreEntries}>
                                    <span>Load more boards</span>
                                    <i className="fa fa-arrow-down" aria-hidden="true" />
                                </button>
                            )}
                        </aside>
                    </Col>
                    <Col xs="10" className="TaskListColumn">
                        <TaskManagementViewContainer currentUser={currentUser} defaultColumnConfig={defaultColumnConfig} tablePreferenceId={tablePreferenceId} />
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default graphql(AllBoardsQuery, {
    options: ({ searchTerm }) => ({
        variables: {
            searchTerm,
            orderBy: ["date_added"],
            isArchived: false,
            first: 10
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network'
    }),
    props: ({ data: { loading, allBoards, fetchMore, searchTerm, variables, refetch } }) => (
        {
            loading,
            allBoards,
            variables,
            refetch,
            refetchDriversQuery: () => {
                //No apollo function that refetches in place with pagination considered so this function instead
                let currentLength = allBoards && allBoards.edges ? allBoards.edges.length : 1
                return fetchMore({
                    query: AllBoardsQuery,
                    variables: {
                        ...variables,
                        first: currentLength,
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allBoards.edges
                        const pageInfo = fetchMoreResult.allBoards.pageInfo
                        return {
                            allBoards: {
                                edges: [...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allBoards.__typename
                            },
                        }
                    },
                })
            },
            loadMoreEntries: () => {
                return fetchMore({
                    query: AllBoardsQuery,
                    variables: {
                        cursor: allBoards.pageInfo.endCursor,
                        ...variables
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allBoards.edges
                        const pageInfo = fetchMoreResult.allBoards.pageInfo

                        return {
                            // Put the new comments at the end of the list and update `pageInfo`
                            // so we have the new `endCursor` and `hasNextPage` values
                            allBoards: {
                                edges: [...previousResult.allBoards.edges, ...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allBoards.__typename
                            },
                        }
                    },
                })
            },
        }
    )
})(withApolloClient(TaskManagementContainer))
